import * as R from 'ramda';
import { PackQuantitiesWithMultiplier } from '../../../../services/modules/product-colors-packs/product-colors-packs.types';
import { AvailabilityEnum, ESProductColor, ProductPageDTO } from '../../../products/ProductTypes';
import { OrderProductSelectedPack } from '../../../products/details/productDetails.types';
import {
  UpdateDeliveryWindowPayload,
  UpdateErrorParams,
  UpdateOrderProductGridPayload,
} from '../interfaces/add-order-product-form-state.types';
import {
  AddOrderProductFormState,
  AddOrderProductKit,
  MinimumAddOrderProductState,
  UpdateItemFormat,
} from '../orderProducts.types';
import { calculateTotalAddOrderProduct } from '../orderProducts.utils';
import { SuggestedGridItem } from '../product/suggestedGrid/suggested-grid.interface';

const defaultResetState: MinimumAddOrderProductState = {
  deliveryWindow: undefined,
  availability: AvailabilityEnum.EMPTY,
  totals: {
    partsQuantity: 0,
    total: 0,
    itemsQuantity: 0,
  },
};

export const initialState: AddOrderProductFormState = {
  createOrderItems: [],
  product: null,
  kit: null,
  color: null,
  colors: [],
  loading: false,
  inputProductValue: '',
  grid: [],
  suggestedGrid: undefined,
  suggestedPacks: undefined,
  loadingColors: false,
  hasError: false,
  errorMessage: undefined,
  submitButton: {
    isSubmitting: true,
    status: 'loading',
  },
  hasValidPacks: false,
  selectedPacks: [],
  ...defaultResetState,
};

const buildOnChangeProductInputState = (
  state: AddOrderProductFormState,
  value: string,
): AddOrderProductFormState => {
  if (value === state.inputProductValue) {
    return {
      ...state,
      inputProductValue: value,
    };
  }

  return {
    ...state,
    inputProductValue: value,
    hasError: false,
    errorMessage: undefined,
    product: null,
    kit: null,
    color: null,
    colors: [],
    grid: [],
    createOrderItems: [],
    selectedPacks: [],
    suggestedPacks: [],
    hasValidPacks: false,
    ...defaultResetState,
  };
};

const applyOrderItemUpdate = (
  state: AddOrderProductFormState,
  updateItem: UpdateItemFormat,
): AddOrderProductFormState => {
  const foundIndex = state.createOrderItems.findIndex(({ skuId }) => skuId === updateItem.skuId);
  const newOrderItems =
    foundIndex !== -1
      ? state.createOrderItems.map((item, index) => (index === foundIndex ? updateItem : item))
      : [...state.createOrderItems, updateItem];

  const newGrid = state.grid.map((gridItem) =>
    gridItem.skuId === updateItem.skuId
      ? { ...gridItem, orderItemQuantity: updateItem.quantity || 0 }
      : gridItem,
  );

  return {
    ...state,
    createOrderItems: newOrderItems,
    grid: newGrid,
    totals: {
      itemsQuantity: 1,
      partsQuantity: R.sum(newOrderItems.map((orderItem) => orderItem.quantity || 0)),
      total: calculateTotalAddOrderProduct(newOrderItems, state.grid),
    },
  };
};

export const reducer = (state: AddOrderProductFormState, action: any): AddOrderProductFormState => {
  switch (action.type) {
    case 'ON_CHANGE_PRODUCT_INPUT': {
      return buildOnChangeProductInputState(state, action.payload);
    }

    case 'RESET_COLORS': {
      return {
        ...state,
        color: null,
        colors: [],
        deliveryWindow: undefined,
        availability: AvailabilityEnum.EMPTY,
      };
    }

    case 'COLORS_FETCH_RESULT': {
      if (action.payload.ok) {
        return {
          ...state,
          colors: action.payload.data,
          loadingColors: false,
        };
      }
      return {
        ...state,
        loadingColors: false,
      };
    }

    case 'UPDATE_COLOR': {
      return {
        ...state,
        color: action.payload,
        ...defaultResetState,
      };
    }

    case 'UPDATE_PRODUCT': {
      const isSameProduct = action.payload?.id === state.product?.id;

      return {
        ...state,
        product: action.payload,
        kit: null,
        color: null,
        colors: isSameProduct ? state.colors : [],
        loadingColors: isSameProduct ? state.loadingColors : true,
        hasError: false,
        loading: false,
        ...defaultResetState,
      };
    }

    case 'UPDATE_KIT': {
      return {
        ...state,
        kit: action.payload,
        product: null,
        color: null,
        colors: [],
        grid: [],
        createOrderItems: [],
        selectedPacks: [],
        suggestedPacks: [],
        hasValidPacks: false,
        loadingColors: false,
        hasError: false,
        loading: false,
        ...defaultResetState,
      };
    }

    case 'UPDATE_DELIVERY_WINDOW': {
      const { payload }: { payload: UpdateDeliveryWindowPayload } = action;

      if (payload.availability !== AvailabilityEnum.READY_FOR_DELIVERY) {
        return {
          ...state,
          deliveryWindow: payload.deliveryWindow,
          availability: payload.availability,
          createOrderItems: state.createOrderItems.map((item) => ({
            ...item,
            availability: payload.availability,
            deliveryWindowId: payload.deliveryWindow?.id ?? null,
          })),
        };
      }

      return {
        ...state,
        availability: payload.availability,
        deliveryWindow: undefined,
        createOrderItems: state.createOrderItems.map((item) => ({
          ...item,
          availability: payload.availability,
          deliveryWindowId: null,
        })),
      };
    }

    case 'UPDATE_ORDER_ITEMS': {
      const { updateItem }: { updateItem: UpdateItemFormat } = action.payload;

      return applyOrderItemUpdate(state, updateItem);
    }

    case 'UPDATE_LOADING_PRODUCT': {
      return {
        ...state,
        loading: action.payload,
      };
    }

    case 'UPDATE_LOADING_COLORS': {
      return {
        ...state,
        loadingColors: action.payload,
      };
    }

    case 'UPDATE_ERROR': {
      const { hasError, errorMessage } = action.payload;

      if (!hasError) {
        return {
          ...state,
          hasError,
          errorMessage: undefined,
          loading: false,
        };
      }

      return {
        ...state,
        hasError,
        errorMessage,
        loading: false,
        product: null,
        kit: null,
        color: null,
        colors: [],
        grid: [],
        createOrderItems: [],
        selectedPacks: [],
        suggestedPacks: [],
        hasValidPacks: false,
        ...defaultResetState,
      };
    }

    case 'UPDATE_ORDER_PRODUCT_GRID': {
      const { grid, hasValidPacks, selectedPacks, suggestedPacks } =
        action.payload as UpdateOrderProductGridPayload;
      return {
        ...state,
        grid,
        hasValidPacks,
        selectedPacks,
        suggestedPacks,
        totals: initialState.totals,
      };
    }

    case 'RESET_ORDER_PRODUCT_GRID': {
      return {
        ...state,
        grid: [],
        createOrderItems: [],
        selectedPacks: [],
        suggestedPacks: [],
        hasValidPacks: false,
      };
    }

    case 'UPDATE_SUGGESTED_GRID': {
      return { ...state, suggestedGrid: action.payload };
    }

    case 'UPDATE_ORDER_PRODUCT_WITH_GRID': {
      const { updateItems }: { updateItems: UpdateItemFormat[] } = action.payload;

      const newGrid = state.grid.map((gridItem, index) => ({
        ...gridItem,
        orderItemQuantity: updateItems[index].quantity,
      }));

      return {
        ...state,
        createOrderItems: updateItems,
        totals: {
          itemsQuantity: 1,
          partsQuantity: R.sum(updateItems.map((orderItem) => orderItem.quantity || 0)),
          total: calculateTotalAddOrderProduct(updateItems, state.grid),
        },
        grid: newGrid,
      };
    }

    case 'RESET': {
      return initialState;
    }

    case 'RESET_GRID_AND_DELIVERY_WINDOW': {
      return {
        ...state,
        grid: [],
        hasValidPacks: false,
        selectedPacks: [],
        suggestedPacks: [],
        deliveryWindow: undefined,
      };
    }

    case 'RESET_SELECTED_PACKS': {
      return {
        ...state,
        selectedPacks: [],
      };
    }

    case 'UPDATE_SELECTED_PACKS': {
      const { packs }: { packs: PackQuantitiesWithMultiplier[] } = action.payload;
      const selectedPacks: OrderProductSelectedPack[] = packs.map((pack) => ({
        packId: pack.id,
        packName: pack.name,
        quantity: pack.multiplier || 1,
        packQuantitiesScreenshot: [],
      }));

      return {
        ...state,
        selectedPacks,
      };
    }

    default:
      throw new Error('Nenhuma ação encontrada');
  }
};

export const onChangeProductInput = (payload: string) => ({
  type: 'ON_CHANGE_PRODUCT_INPUT',
  payload,
});

export const resetColors = () => ({ type: 'RESET_COLORS' });

export const updateColor = (payload: ESProductColor | null) => ({ type: 'UPDATE_COLOR', payload });

export const updateProduct = (payload: Pick<ProductPageDTO, 'id' | 'code' | 'name'> | null) => ({
  type: 'UPDATE_PRODUCT',
  payload,
});

export const updateKit = (payload: AddOrderProductKit) => ({
  type: 'UPDATE_KIT',
  payload,
});

export const updateOrderItems = (payload: { updateItem: UpdateItemFormat }) => ({
  type: 'UPDATE_ORDER_ITEMS',
  payload,
});

export const colorsFetchResult = (payload: any) => ({ type: 'COLORS_FETCH_RESULT', payload });

export const updateDeliveryWindow = (payload: UpdateDeliveryWindowPayload) => ({
  type: 'UPDATE_DELIVERY_WINDOW',
  payload,
});

export const updateLoadingProduct = (payload: boolean) => ({
  type: 'UPDATE_LOADING_PRODUCT',
  payload,
});

export const updateLoadingColors = (payload: boolean) => ({
  type: 'UPDATE_LOADING_COLORS',
  payload,
});

export const updateError = (payload: UpdateErrorParams) => ({
  type: 'UPDATE_ERROR',
  payload,
});

export const resetOrderProductGrid = () => ({ type: 'RESET_ORDER_PRODUCT_GRID' });

export const updateOrderProductGrid = (payload: UpdateOrderProductGridPayload) => ({
  type: 'UPDATE_ORDER_PRODUCT_GRID',
  payload,
});

export const updateSuggestedGrid = (payload: SuggestedGridItem[]) => ({
  type: 'UPDATE_SUGGESTED_GRID',
  payload,
});

export const updateOrderProductWithGrid = (payload: { updateItems: UpdateItemFormat[] }) => ({
  type: 'UPDATE_ORDER_PRODUCT_WITH_GRID',
  payload,
});

export const resetGridAndDeliveryWindow = () => ({ type: 'RESET_GRID_AND_DELIVERY_WINDOW' });

export const resetAddOrderProduct = () => ({ type: 'RESET' });

export const resetSelectedPacks = () => ({ type: 'RESET_SELECTED_PACKS' });

export const updateSelectedPacks = (payload: { packs: PackQuantitiesWithMultiplier[] }) => ({
  type: 'UPDATE_SELECTED_PACKS',
  payload,
});
